import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import type { DiscussionReactionSummary } from '../../services/classroom/teacherService';

export const QUICK_DISCUSSION_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const;

function HeartPlusIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M19 14h-2v2h-2v-2h-2v-2h2v-2h2v2h2zm-6.65 4.17C11.86 18.69 11.16 19.23 10.4 19.88C5.5 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5c0 .95-.22 1.84-.6 2.65c-.5-.1-1.02-.15-1.55-.15c-.34 0-.68.03-1.01.08C18.95 10.5 19 9.5 19 8.5C19 7.12 17.88 6 16.5 6c-1.12 0-2.08.58-2.61 1.44L13 8.5l-.89-1.06C11.58 6.58 10.62 6 9.5 6C8.12 6 7 7.12 7 8.5c0 2.54 2.47 5.17 5.35 7.67Z"
      />
    </svg>
  );
}

type DiscussionReactionBarProps = {
  reactions?: DiscussionReactionSummary[] | null;
  disabled?: boolean;
  onToggleReaction: (emoji: string) => void;
};

const Root = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  margin: 0.35rem 0 0.15rem;
`;

const Chip = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(37, 99, 235, 0.45)' : 'rgba(15, 23, 42, 0.12)')};
  background: ${({ $active }) => ($active ? 'rgba(37, 99, 235, 0.1)' : 'rgba(248, 250, 252, 0.95)')};
  color: ${({ $active }) => ($active ? '#1d4ed8' : '#334155')};
  border-radius: 999px;
  padding: 0.2rem 0.55rem;
  font-size: 0.82rem;
  line-height: 1.2;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AddWrap = styled.div`
  position: relative;
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.85rem;
  height: 1.85rem;
  border-radius: 999px;
  border: 1px dashed rgba(15, 23, 42, 0.22);
  background: #fff;
  color: #64748b;
  line-height: 1;
  padding: 0;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Picker = styled.div`
  position: absolute;
  bottom: calc(100% + 0.35rem);
  left: 0;
  z-index: 20;
  display: flex;
  gap: 0.25rem;
  padding: 0.4rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: #fff;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
`;

const PickerButton = styled.button`
  border: none;
  background: transparent;
  font-size: 1.15rem;
  line-height: 1;
  padding: 0.25rem;
  border-radius: 0.45rem;
  cursor: pointer;

  &:hover {
    background: rgba(15, 23, 42, 0.06);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export function DiscussionReactionBar({
  reactions,
  disabled = false,
  onToggleReaction,
}: DiscussionReactionBarProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const normalized = (reactions ?? []).filter(
    (reaction) => reaction.emoji.trim().length > 0 && reaction.count > 0
  );

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setPickerOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPickerOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [pickerOpen]);

  return (
    <Root ref={rootRef}>
      {normalized.map((reaction) => (
        <Chip
          key={reaction.emoji}
          type="button"
          $active={reaction.reactedByMe}
          disabled={disabled}
          onClick={() => onToggleReaction(reaction.emoji)}
          aria-pressed={reaction.reactedByMe}
          aria-label={`React with ${reaction.emoji}`}
        >
          <span aria-hidden>{reaction.emoji}</span>
          <span>{reaction.count}</span>
        </Chip>
      ))}
      <AddWrap>
        <AddButton
          type="button"
          disabled={disabled}
          onClick={() => setPickerOpen((open) => !open)}
          aria-expanded={pickerOpen}
          aria-label="Add reaction"
        >
          <HeartPlusIcon />
        </AddButton>
        {pickerOpen ? (
          <Picker role="listbox" aria-label="Choose reaction">
            {QUICK_DISCUSSION_REACTIONS.map((emoji) => (
              <PickerButton
                key={emoji}
                type="button"
                disabled={disabled}
                onClick={() => {
                  setPickerOpen(false);
                  onToggleReaction(emoji);
                }}
              >
                {emoji}
              </PickerButton>
            ))}
          </Picker>
        ) : null}
      </AddWrap>
    </Root>
  );
}
